import EmployeeLayout from "../EmployeeLayout";
import {
  useUser,
  useProjects,
  useGetPermissions,
  useFilterEmployee,
  useTotalStaff
} from "../../Use-auth";
import { getStatusColor } from "../utils/taskUtils";
import moment from "moment";
import { useDateRange } from "../DateRangeContext";
import {
  UserRound,
  TableCellsSplit,
  Star,
  Volleyball,
  AlignStartVertical,
  CircleCheck
} from "lucide-react";
import TaskEmployees from "../../ui/TaskEmployees";
import TaskPriority from "../../ui/TaskPriority";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { useLocation } from "wouter";

import {
  TrendingUp,
  EllipsisVertical
} from "lucide-react";
import SkeletonPage from "../../ui/SkeletonPage ";
import React, { useEffect, useState, useMemo } from "react";
import { Link } from "wouter";
import Card from "../../ui/Cards";
import ChartCard from "../../ui/ChartCard";
import { tailwindBgToHex } from "../utils/taskUtils";

const chartConfigs = {
  daily: {
    task: {
      title: "Today's Task",
      icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
      description: "Task today",
    },
    project: {
      title: "Today's Project",
      icon: <Volleyball className="w-16 h-16 mx-auto text-green-500 mb-2" />,
      description: "Task today",
    },
  },

  weekly: {
    task: {
      title: "Weekly Task",
      icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
      description: "Task this week",
    },
    project: {
      title: "Weekly Project",
      icon: <Volleyball className="w-16 h-16 mx-auto text-green-500 mb-2" />,
      description: "Task today",
    },
  },

  monthly: {
    task: {
      title: "Monthly Task",
      icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
      description: "Task this month",
    },
    project: {
      title: "Monthly Project",
      icon: <Volleyball className="w-16 h-16 mx-auto text-green-500 mb-2" />,
      description: "Task this month",
    },
  },

  yearly: {
    task: {
      title: "Yearly Task",
      icon: <TrendingUp className="w-16 h-16 mx-auto text-blue-500 mb-2" />,
      description: "Task this year",
    },
    project: {
      title: "Yearly Project",
      icon: <Volleyball className="w-16 h-16 mx-auto text-green-500 mb-2" />,
      description: "Project this year",
    },
  },
};

const formatStatusLabel = (status = "") =>
  status
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());

const hasActiveTasks = (statusTask = {}) => {
  return Object.values(statusTask).some(tasks =>
    tasks.some(t => t.status !== "complete")
  );
};

export default function EmployeeDashboard() {
  const { start, end, rangeType } = useDateRange();
  const { data: employees, isLoading: employeesLoading } = useTotalStaff();
  const { data: projects = [], isLoading: projectsLoading } = useProjects();
  const isTasksLoading = projectsLoading || employeesLoading;
  const [, navigate] = useLocation();

  const { data: user } = useUser();
  const { data: permissions = [] } = useGetPermissions();

  const currentUserPermissions = permissions.find(
    p => p.userId === user?.userId
  );

  const canViewHome =
    user?.department === "Admin" ||
    currentUserPermissions?.employees?.home_view === true;

  const isWithinRange = (date) => {
    if (!start || !end) return true;
    const projectDate = moment(date);
    return projectDate.isBetween(moment(start), moment(end), undefined, '[]');
  };

  const filteredProjects = useMemo(() => {
    return projects.filter((p) => {
      const dateOk = isWithinRange(p.createdAt || p.updatedAt);

      const isShared = p.sharedWith?.includes(user?.userId);
      const isCreator = p.createdBy?.userId === user?.userId;
      const isAdmin = ["Admin", "Management"].includes(user?.department);

      return dateOk && (isShared || isCreator || isAdmin);
    });
  }, [projects, user?.userId, user?.department, start, end]);

  const filteredLostProjects = filteredProjects.filter(p => p.status === "Lost");

  const completedTasks = filteredProjects.filter(p => p.status === "Completed")

  function getAllTasks(project, parentId = null, parentName = null) {
    let tasks = [];

    const statusTask = project.statusTask || {};

    Object.entries(statusTask).forEach(([status, arr]) => {
      arr.forEach(task => {
        tasks.push({
          ...task,
          projectId: parentId || project._id,

          projectName: parentName
            ? `${parentName} / ${project.projectName}`
            : project.projectName,

          status,
        });
      });
    });

    if (project.subProjects?.length) {
      project.subProjects.forEach(sub => {
        tasks = tasks.concat(
          getAllTasks(
            sub,
            project._id,
            parentName ? `${parentName} / ${project.projectName}` : project.projectName
          )
        );
      });
    }

    return tasks;
  }

  const allTasks = filteredProjects
    .flatMap(p => getAllTasks(p))
    .filter(task => {
      const taskDate = new Date(
        task.updatedAt || task.createdAt
      );

      return taskDate >= start && taskDate <= end;
    });

  const assignedTasks = allTasks.filter(task =>
    task.assignedEmployees?.some(
      emp => emp.userId === user?.userId
    )
  );

  const totalAssignedTasks = assignedTasks.length;

  const assignedProjects = [
    ...new Set(assignedTasks.map(t => t.projectId)),
  ];

  const totalProjects = assignedProjects.length;

  const activeProjects = filteredProjects.filter(p => {
    if (p.status === "Lost") return false;

    let active = getAllTasks(p).some(task => {
      const d = new Date(task.updatedAt || task.createdAt);
      return (
        d >= start &&
        d <= end &&
        task.status !== "complete"
      );
    });

    if (!active && p.subProjects?.length) {
      active = p.subProjects.some(sp =>
        hasActiveTasks(sp.statusTask)
      );
    }

    return active;
  });

  let myTasks = filteredProjects
    .flatMap(p => getAllTasks(p))
    .filter(task => {
      const taskDate = new Date(
        task.updatedAt || task.createdAt
      );

      const inRange =
        taskDate >= start && taskDate <= end;

      const isMine =
        task.assignedEmployees?.some(
          emp => emp.userId === user?.userId
        );

      const isAdmin =
        ["Admin", "Management"].includes(user?.department);

      return inRange && (isMine || isAdmin);
    });

  myTasks.slice(0, 10).sort((a, b) => {
    const d1 = new Date(a.updatedAt || a.createdAt);
    const d2 = new Date(b.updatedAt || b.createdAt);
    return d2 - d1;
  });

  const currentChart = chartConfigs[rangeType] || chartConfigs.daily;

  const chartData = (() => {
    if (!start || !end) return [];

    let data = [];
    if (rangeType === "yearly") {
      for (let i = 0; i < 12; i++) {

        const monthStart = new Date(start.getFullYear(), i, 1);
        const monthEnd = new Date(start.getFullYear(), i + 1, 0);

        const monthName = monthStart.toLocaleString("en-IN", {
          month: "short",
        });

        const monthTasks = myTasks.filter(t => {
          const d = new Date(t.updatedAt || t.createdAt);
          return d >= monthStart && d <= monthEnd;
        });

        data.push({
          name: monthName,
          assigned: monthTasks.length,
          completed: monthTasks.filter(t => t.status === "complete").length,
        });
      }

      return data;
    }

    let tempDate = new Date(start);

    while (tempDate <= end) {

      const label = tempDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      });

      const dayTasks = myTasks.filter(t => {
        const d = new Date(t.updatedAt || t.createdAt);
        return d.toDateString() === tempDate.toDateString();
      });

      data.push({
        name: label,
        assigned: dayTasks.length,
        completed: dayTasks.filter(t => t.status === "complete").length,
      });

      tempDate.setDate(tempDate.getDate() + 1);
    }

    return data;
  })();

  const projectChartData = (() => {
    if (!start || !end) return [];

    let data = [];

    if (rangeType === "yearly") {

      for (let i = 0; i < 12; i++) {

        const monthStart = new Date(start.getFullYear(), i, 1);
        const monthEnd = new Date(start.getFullYear(), i + 1, 0);

        const monthName = monthStart.toLocaleString("en-IN", {
          month: "short",
        });

        const activeInMonth = filteredProjects.filter(p => {
          if (p.status === "Lost") return false;

          return getAllTasks(p).some(task => {
            const d = new Date(task.updatedAt || task.createdAt);

            return (
              d >= monthStart &&
              d <= monthEnd &&
              task.status !== "complete"
            );
          });
        });

        data.push({
          name: monthName,
          total: totalProjects,
          active: activeInMonth.length,
        });
      }

      return data;
    }

    let tempDate = new Date(start);

    while (tempDate <= end) {

      const label = tempDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
      });

      const activeOnDay = filteredProjects.filter(p => {
        if (p.status === "Lost") return false;

        return getAllTasks(p).some(task => {
          const d = new Date(task.updatedAt || task.createdAt);

          return (
            d.toDateString() === tempDate.toDateString() &&
            task.status !== "complete"
          );
        });
      });

      data.push({
        name: label,
        total: totalProjects,
        active: activeOnDay.length,
      });

      tempDate.setDate(tempDate.getDate() + 1);
    }

    return data;
  })();

  myTasks = myTasks.filter(
    (task) => task.status !== "completed" && task.status !== "complete"
  );

  myTasks = myTasks
    .sort((a, b) => {
      const d1 = new Date(a.updatedAt || a.createdAt);
      const d2 = new Date(b.updatedAt || b.createdAt);
      return d2 - d1;
    })
    .slice(0, 10);

  const groupedTasks = React.useMemo(() => {
    return myTasks.reduce((acc, task) => {
      const name = task.projectName || "Unknown Project";

      if (!acc[name]) {
        acc[name] = [];
      }

      acc[name].push(task);

      return acc;
    }, {});
  }, [myTasks]);


  const onTaskClick = (projectId, subProjectId, taskId) => {
    navigate(
      `/project?tab=list&projectId=${projectId}&subProjectId=${subProjectId || ""}&taskId=${taskId || ""}`
    );
  };

  const getIconColorFromStatus = (status,) => {

    const bgClass = getStatusColor(status);

    const bg = bgClass.split(" ")[0];

    return tailwindBgToHex[bg] || "#9ca3af";
  };

  return (
    <EmployeeLayout>
      <div className="p-2 rs:p-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6 mb-4 sm:mb-8">
          <Link href="/project?tab=list&filter=all"> <Card title="Total Projects" value={canViewHome ? filteredProjects.length : "-"} icon={<UserRound className="opacity-80" />} bg="from-blue-500 to-blue-600" /></Link>
          <Link href="/project?tab=mytasks&filter=all"><Card title="Assigned Tasks" value={canViewHome ? totalAssignedTasks : "-"} icon={<TableCellsSplit className="opacity-80" />} bg="from-purple-500 to-purple-600" /></Link>
          <Link href="/project?tab=list&filter=Completed"><Card title="Completed Projects" value={canViewHome ? completedTasks.length : "-"} icon={<Star className="opacity-80" />} bg="from-orange-500 to-orange-600" /></Link>
          <Link href="/project?tab=list&filter=Lost"><Card title="Lost Projects" value={canViewHome ? filteredLostProjects.length : "-"} icon={<AlignStartVertical className="opacity-80" />} bg="from-green-500 to-green-600" /></Link>
        </div>
        {canViewHome && (
          <div className="bg-white rounded-lg sm:rounded-2xl p-2 sm:p-6 shadow-lg">
            <h3 className="font-bold text-sm sm:text-xl mb-4">Recent Tasks</h3>

            {isTasksLoading ? (
              <SkeletonPage />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm">
                  <thead className="border-b ">
                    <tr>
                      <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">Title</th>
                      <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden zx:table-cell">Status</th>
                      <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm">Priority</th>
                      <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden zx:table-cell">Assigned</th>
                      <th className="px-2 py-1 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider lg:px-3 lg:py-2 lg:text-sm hidden custom:table-cell">Updated</th>
                    </tr>
                  </thead>

                  <tbody>
                    {Object.keys(groupedTasks).length > 0 ? (
                      Object.entries(groupedTasks).map(([projectName, tasks]) => (
                        <React.Fragment key={projectName}>
                          <tr className={` border-b border-gray-200 bg-[#dfeaf6]`}>
                            <td
                              colSpan="7"
                              className="rounded-md px-1 py-1 lg:px-3 lg:py-2 font-medium text-gray-900 text-lg border-b border-gray-200"
                            >
                              <div className="flex items-center gap-2 sm:gap-3">
                                <span className=" text-black px-1 sm:px-3 py-0 sm:py-1 rounded sm:rounded-lg text-[12px] sm:text-[16px] font-semibold">
                                  {projectName}
                                </span>

                                <span className="text-gray-600 text-xs lg:text-sm">
                                  {tasks.length} Tasks
                                </span>
                              </div>
                            </td>
                          </tr>

                          {/* Tasks */}
                          {tasks.map(task => (
                            <tr key={task._id} className="border-b text-xs lg:text-sm text-gray-600 cursor-pointer" onClick={() =>
                              onTaskClick(task.projectId, task.subProjectId, task._id, task.status)
                            }>
                              <td className="px-2 py-1 text-left text-xs lg:text-sm text-gray-600 max-w-[250px]">
                                <div className="flex items-center gap-2">
                                  <span className={`font-bold`}>
                                    <CircleCheck
                                      className="w-4 h-4"
                                      color={getIconColorFromStatus(task.status)}
                                    />
                                  </span>
                                  <span className="break-words">
                                    {(task?.title || "").length > 20
                                      ? `${task.title.slice(0, 20)}...`
                                      : (task.title || "Untitled")}
                                  </span>
                                </div>
                              </td>

                              <td className="px-2 py-1 hidden zx:table-cell">
                                <span
                                  className={`${getStatusColor(task.status)} text-white px-2 py-1 rounded sm:rounded-lg text-xs sm:text-sm`}
                                >
                                  {formatStatusLabel(task.status)}
                                </span>
                              </td>

                              <td className="px-2 py-1">
                                <TaskPriority value={task.priority} showclear={false} />
                              </td>

                              <td className="px-2 py-1 hidden zx:table-cell">
                                <TaskEmployees
                                  selected={task.assignedEmployees?.map((e) => e.userId) || []}
                                  employees={employees || []}
                                  showDropdown={false}
                                  hideSelected
                                />
                              </td>

                              <td className="px-2 py-1 hidden custom:table-cell text-xs sm:text-sm">
                                {new Date(task.updatedAt || task.createdAt).toLocaleString()}
                              </td>
                            </tr>
                          ))}
                        </React.Fragment>
                      ))
                    ) : (
                      <tr>
                        <td
                          colSpan="5"
                          className="text-center py-4 sm:py-6 text-gray-400 text-xs sm:text-sm"
                        >
                          No tasks found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
          </div>)}

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-2 gap-3 sm:gap-6 mt-4 sm:mt-8">
          <ChartCard title={currentChart.task.title} icon={currentChart.task.icon} description={currentChart.task.description}
          >
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={canViewHome ? chartData : []} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}>
                <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#555" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#555" }} width={30} />
                <Tooltip />
                <Line type="monotone" dataKey="assigned" stroke="#ef6210" strokeWidth={2} name="Assigned Tasks" dot={{ r: 5 }} />
                <Line type="monotone" dataKey="completed" stroke="#1cb554" strokeWidth={2} name="Completed Tasks" dot={{ r: 5 }} />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
          <ChartCard title={currentChart.project.title} icon={currentChart.project.icon} description={currentChart.project.description}>
            <ResponsiveContainer width="100%" height={220}>
              <LineChart data={canViewHome ? projectChartData : []} margin={{ top: 20, right: 30, left: 0, bottom: 5 }}  >
                <CartesianGrid stroke="#000" strokeOpacity={0.1} strokeDasharray="3 3" />
                <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#555" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "#555" }} width={30} />
                <Tooltip />
                <Line type="monotone" dataKey="total" stroke="#8b5cf6" strokeWidth={2} name="Total Projects" dot={{ r: 5 }} />
                <Line type="monotone" dataKey="active" stroke="#22c55e" strokeWidth={2} name="Active Projects" dot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      </div>
    </EmployeeLayout>
  );
}
